import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useCloseOnBack } from '../hooks/useCloseOnBack';
import { useTranslation } from '../i18n';
import { ProductImage, productImageSrc } from '../types';

const MIN_SCALE = 1;
const MAX_SCALE = 5;
const ZOOM_STEP = 0.35;
const DOUBLE_TAP_MS = 300;

interface ImageLightboxProps {
  open: boolean;
  onClose: () => void;
  images: ProductImage[];
  initialIndex?: number;
  alt?: string;
}

export const ImageLightbox: React.FC<ImageLightboxProps> = ({
  open,
  onClose,
  images,
  initialIndex = 0,
  alt = 'Product',
}) => {
  const { t } = useTranslation();
  const [idx, setIdx] = useState(initialIndex);
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const scaleRef = useRef(scale);
  const offsetRef = useRef(offset);
  scaleRef.current = scale;
  offsetRef.current = offset;

  const dragging = useRef(false);
  const didDrag = useRef(false);
  const lastPointer = useRef({ x: 0, y: 0 });
  const lastTap = useRef(0);
  const pinchStartDist = useRef<number | null>(null);
  const pinchStartScale = useRef(1);
  const stageRef = useRef<HTMLDivElement>(null);

  const multi = images.length > 1;
  const clampedIdx = images.length === 0 ? 0 : ((idx % images.length) + images.length) % images.length;
  const src = images[clampedIdx] ? productImageSrc(images[clampedIdx], 'original') : undefined;

  useCloseOnBack(open, onClose);

  const resetView = useCallback(() => {
    setScale(1);
    setOffset({ x: 0, y: 0 });
  }, []);

  const zoomTo = useCallback((target: number, originX?: number, originY?: number) => {
    const prev = scaleRef.current;
    const next = Math.min(MAX_SCALE, Math.max(MIN_SCALE, target));
    if (next === prev) return;

    if (next === MIN_SCALE) {
      setScale(MIN_SCALE);
      setOffset({ x: 0, y: 0 });
      return;
    }

    if (originX != null && originY != null && stageRef.current) {
      const rect = stageRef.current.getBoundingClientRect();
      const cx = originX - rect.left - rect.width / 2;
      const cy = originY - rect.top - rect.height / 2;
      const ratio = next / prev;
      const o = offsetRef.current;
      setOffset({
        x: cx - (cx - o.x) * ratio,
        y: cy - (cy - o.y) * ratio,
      });
    }
    setScale(next);
  }, []);

  const zoomBy = useCallback(
    (delta: number, originX?: number, originY?: number) => {
      zoomTo(scaleRef.current + delta, originX, originY);
    },
    [zoomTo]
  );

  useEffect(() => {
    if (!open) return;
    setIdx(initialIndex);
    resetView();
  }, [open, initialIndex, resetView]);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopImmediatePropagation();
        onClose();
        return;
      }
      if (!multi) return;
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        setIdx(i => (i - 1 + images.length) % images.length);
        resetView();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        setIdx(i => (i + 1) % images.length);
        resetView();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [open, onClose, multi, images.length, resetView]);

  useEffect(() => {
    resetView();
  }, [clampedIdx, resetView]);

  useEffect(() => {
    if (!open) return;
    const stage = stageRef.current;
    if (!stage) return;
    const onWheelNative = (e: WheelEvent) => {
      e.preventDefault();
      const delta = e.deltaY < 0 ? ZOOM_STEP : -ZOOM_STEP;
      zoomBy(delta, e.clientX, e.clientY);
    };
    stage.addEventListener('wheel', onWheelNative, { passive: false });
    return () => stage.removeEventListener('wheel', onWheelNative);
  }, [open, zoomBy, src]);

  const onPointerDown = (e: React.PointerEvent) => {
    if (e.pointerType === 'touch') return;
    didDrag.current = false;
    lastPointer.current = { x: e.clientX, y: e.clientY };
    if (scale > MIN_SCALE) {
      dragging.current = true;
      (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
    }
  };

  const onPointerMove = (e: React.PointerEvent) => {
    const dx = e.clientX - lastPointer.current.x;
    const dy = e.clientY - lastPointer.current.y;
    if (Math.hypot(dx, dy) > 4) didDrag.current = true;
    if (!dragging.current) return;
    lastPointer.current = { x: e.clientX, y: e.clientY };
    setOffset(o => ({ x: o.x + dx, y: o.y + dy }));
  };

  const onPointerUp = (e: React.PointerEvent) => {
    if (e.pointerType === 'touch') {
      dragging.current = false;
      return;
    }
    const wasDragging = dragging.current;
    dragging.current = false;
    if (didDrag.current && wasDragging) return;
    if (scale <= MIN_SCALE) {
      zoomTo(MAX_SCALE, e.clientX, e.clientY);
    } else {
      resetView();
    }
  };

  const touchDistance = (touches: React.TouchList) => {
    const a = touches[0];
    const b = touches[1];
    return Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
  };

  const onTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length === 2) {
      pinchStartDist.current = touchDistance(e.touches);
      pinchStartScale.current = scale;
      return;
    }
    if (e.touches.length === 1) {
      const now = Date.now();
      if (now - lastTap.current < DOUBLE_TAP_MS) {
        if (scale > MIN_SCALE) resetView();
        else zoomTo(MAX_SCALE, e.touches[0].clientX, e.touches[0].clientY);
        lastTap.current = 0;
      } else {
        lastTap.current = now;
      }
      lastPointer.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
      didDrag.current = false;
      dragging.current = scale > MIN_SCALE;
    }
  };

  const onTouchMove = (e: React.TouchEvent) => {
    if (e.touches.length === 2 && pinchStartDist.current != null) {
      e.preventDefault();
      const dist = touchDistance(e.touches);
      const next = Math.min(
        MAX_SCALE,
        Math.max(MIN_SCALE, pinchStartScale.current * (dist / pinchStartDist.current))
      );
      setScale(next);
      if (next === MIN_SCALE) setOffset({ x: 0, y: 0 });
      return;
    }
    if (e.touches.length === 1 && dragging.current) {
      e.preventDefault();
      const dx = e.touches[0].clientX - lastPointer.current.x;
      const dy = e.touches[0].clientY - lastPointer.current.y;
      if (Math.hypot(dx, dy) > 4) didDrag.current = true;
      lastPointer.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
      setOffset(o => ({ x: o.x + dx, y: o.y + dy }));
    }
  };

  const onTouchEnd = () => {
    pinchStartDist.current = null;
    dragging.current = false;
  };

  const go = (dir: number) => {
    setIdx(i => (i + dir + images.length) % images.length);
  };

  if (!open || !src) return null;

  return (
    <div
      className="image-lightbox"
      role="dialog"
      aria-modal="true"
      aria-label={t('ui.lightbox_title')}
      onClick={onClose}
    >
      <button
        type="button"
        className="image-lightbox__close"
        onClick={onClose}
        aria-label={t('ui.modal_close')}
      >
        &times;
      </button>

      <div className="image-lightbox__toolbar" onClick={e => e.stopPropagation()}>
        <button
          type="button"
          className="image-lightbox__zoom-btn"
          onClick={() => zoomBy(-ZOOM_STEP)}
          aria-label={t('ui.lightbox_zoom_out')}
          disabled={scale <= MIN_SCALE}
        >
          −
        </button>
        <button
          type="button"
          className="image-lightbox__zoom-btn"
          onClick={() => zoomBy(ZOOM_STEP)}
          aria-label={t('ui.lightbox_zoom_in')}
          disabled={scale >= MAX_SCALE}
        >
          +
        </button>
      </div>

      {multi && (
        <>
          <button
            type="button"
            className="image-lightbox__nav image-lightbox__nav--prev"
            onClick={e => {
              e.stopPropagation();
              go(-1);
            }}
            aria-label={t('ui.lightbox_prev')}
          >
            ‹
          </button>
          <button
            type="button"
            className="image-lightbox__nav image-lightbox__nav--next"
            onClick={e => {
              e.stopPropagation();
              go(1);
            }}
            aria-label={t('ui.lightbox_next')}
          >
            ›
          </button>
          <div className="image-lightbox__counter" onClick={e => e.stopPropagation()}>
            {clampedIdx + 1}/{images.length}
          </div>
        </>
      )}

      <div
        ref={stageRef}
        className="image-lightbox__stage"
        onClick={e => e.stopPropagation()}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        style={{ cursor: scale > MIN_SCALE ? 'zoom-out' : 'zoom-in' }}
      >
        <img
          src={src}
          alt={alt}
          className="image-lightbox__img"
          draggable={false}
          style={{
            transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})`,
          }}
        />
      </div>
    </div>
  );
};
